// PhotoUploader.js
import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { uploadFileToS3 } from "../../common/uploadService";
import MediaPreview from "../../common/MediaPreview";
import { FiEye, FiTrash2, FiUpload, FiCamera, FiX } from "react-icons/fi";
import config from "../../../config";

export default function PhotoUploader({
  value = [],
  onChange,
  maxFiles = 10,
  size = "default", // "default" | "small"
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/gif",
    "image/webp",
  ];

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const sizeClasses = {
    default: {
      uploadAreaRound: "rounded-xl",
      uploadAreaPadding: "p-4",
      iconWrap: "p-3 bg-indigo-100 rounded-full mb-3",
      icon: "w-8 h-8 text-indigo-600",
      spinner: "animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600",
      title: "text-lg font-semibold text-gray-800 mb-1",
      description: "text-sm text-gray-600 mb-3",
      support: "flex items-center gap-2 text-xs text-gray-500",
      supportIcon: "w-4 h-4",
      maxBadge:
        "mt-2 flex items-center gap-1 text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full",
      maxIcon: "w-4 h-4",
      progress: "w-full bg-gray-200 rounded-full h-2",
      progressBar: "bg-indigo-600 h-2 rounded-full animate-pulse w-1/2",
      headerTitle: "font-medium text-gray-700",
      headerSub: "text-sm text-gray-500",
      grid: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4",
      thumbRound: "rounded-lg",
      previewBtn:
        "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 bg-white/90 rounded-full hover:bg-white",
      previewIcon: "w-5 h-5 text-gray-800",
      deleteBtn:
        "absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600",
      deleteIcon: "w-4 h-4",
      indexBadge:
        "absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded",
      addRound: "rounded-lg",
      addIcon: "w-8 h-8 text-gray-400 mb-2",
      addText: "text-sm text-gray-600",
      addRemaining: "text-xs text-gray-500 mt-1",
    },
    small: {
      uploadAreaRound: "rounded-lg",
      uploadAreaPadding: "p-2",
      iconWrap: "p-2 bg-indigo-100 rounded-full mb-2",
      icon: "w-5 h-5 text-indigo-600",
      spinner: "animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600",
      title: "text-sm font-semibold text-gray-800 mb-0.5",
      description: "text-xs text-gray-600 mb-2",
      support: "flex items-center gap-1.5 text-xs text-gray-500",
      supportIcon: "w-3 h-3",
      maxBadge:
        "mt-1.5 flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full",
      maxIcon: "w-3 h-3",
      progress: "w-full bg-gray-200 rounded-full h-1",
      progressBar: "bg-indigo-600 h-1 rounded-full animate-pulse w-1/2",
      headerTitle: "text-sm font-medium text-gray-700",
      headerSub: "text-xs text-gray-500",
      grid: "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2",
      thumbRound: "rounded-md",
      previewBtn:
        "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 bg-white/90 rounded-full hover:bg-white",
      previewIcon: "w-4 h-4 text-gray-800",
      deleteBtn:
        "absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 bg-red-500 text-white rounded-full hover:bg-red-600",
      deleteIcon: "w-3 h-3",
      indexBadge:
        "absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded",
      addRound: "rounded-md",
      addIcon: "w-5 h-5 text-gray-400 mb-1",
      addText: "text-xs text-gray-600",
      addRemaining: "text-xs text-gray-500 mt-0.5",
    },
  };

  const s = sizeClasses[size] || sizeClasses.default;

  const handleFiles = async (files) => {
    if (files.length === 0) return;

    if (value.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} photos allowed`);
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((f) => {
      if (!IMAGE_TYPES.includes(f.type)) {
        invalidFiles.push(`${f.name}: Unsupported file type`);
      } else if (f.size > MAX_FILE_SIZE) {
        invalidFiles.push(`${f.name}: File too large (max 10MB)`);
      } else {
        validFiles.push(f);
      }
    });

    invalidFiles.forEach((msg) => toast.error(msg));

    if (validFiles.length === 0) return;

    setIsUploading(true);
    const uploadedKeys = [];

    try {
      for (const f of validFiles) {
        const tempUrl = URL.createObjectURL(f);

        const s3Key = await uploadFileToS3(f, {
          returnType: "s3Key",
          module: "activity",
          docType: "photo",
        });

        if (s3Key) {
          uploadedKeys.push(s3Key);
          onChange([...(value || []), s3Key]);
        } else {
          toast.error(`${f.name} upload failed`);
        }

        URL.revokeObjectURL(tempUrl);
      }

      toast.success(`Uploaded ${validFiles.length} photo(s) successfully`);
    } catch (err) {
      console.error(err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files || []);
    await handleFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    handleFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const remove = (index) => {
    const arr = [...value];
    arr.splice(index, 1);
    onChange(arr);
    toast.info("Photo removed");
  };

  const openPreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const getPreviewUrl = (photo) => {
    return typeof photo === "string"
      ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${photo}`
      : URL.createObjectURL(photo);
  };

  return (
    <div className="space-y-4">
      {/* Layout container – adapts based on size prop */}
      <div
        className={`${
          size === "small"
            ? "flex flex-row gap-4"
            : "flex flex-col gap-4"
        }`}
      >
        {/* Upload Area */}
        <div
          className={`${
            size === "small" && value.length > 0 ? "w-1/2" : "w-full"
          }`}
        >
          <div
            className={`relative border-2 ${
              dragOver
                ? "border-indigo-500 bg-indigo-50"
                : "border-dashed border-gray-300"
            } ${s.uploadAreaRound} ${s.uploadAreaPadding} transition-all duration-200 ${
              isUploading
                ? "opacity-60"
                : "hover:border-indigo-400 hover:bg-gray-50"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClickUpload}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileInput}
              disabled={isUploading || value.length >= maxFiles}
            />

            <div className="flex flex-col items-center justify-center text-center">
              <div className={s.iconWrap}>
                {isUploading ? (
                  <div className={s.spinner} />
                ) : (
                  <FiUpload className={s.icon} />
                )}
              </div>

              <h3 className={s.title}>
                {isUploading
                  ? "Uploading..."
                  : value.length === 0
                  ? "Upload Photos"
                  : "Add More Photos"}
              </h3>

              <p className={s.description}>
                Drag & drop photos here or click to browse
              </p>

              <div className={s.support}>
                <FiCamera className={s.supportIcon} />
                <span>Supports: JPG, PNG, GIF, WebP</span>
                <span className="mx-2">•</span>
                <span>Max 10MB each</span>
              </div>

              {value.length >= maxFiles && (
                <div className={s.maxBadge}>
                  <FiX className={s.maxIcon} />
                  <span>Maximum {maxFiles} photos reached</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Thumbnails Grid */}
        {value.length > 0 && (
          <div
            className={`${
              size === "small" ? "md:w-2/3" : "w-full"
            } space-y-3`}
          >
            <div className="flex justify-between items-center">
              <h4 className={s.headerTitle}>Uploaded Photos</h4>
              <span className={s.headerSub}>
                Click to preview, hover to delete
              </span>
            </div>

            <div className={s.grid}>
              {value.map((photo, index) => {
                const preview = getPreviewUrl(photo);

                return (
                  <div
                    key={index}
                    className={`relative group aspect-square ${s.thumbRound} overflow-hidden border shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => openPreview(preview)}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/150?text=Error";
                      }}
                    />

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200">
                      <button
                        onClick={() => openPreview(preview)}
                        className={s.previewBtn}
                        title="Preview"
                      >
                        <FiEye className={s.previewIcon} />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(index);
                        }}
                        className={s.deleteBtn}
                        title="Remove photo"
                      >
                        <FiTrash2 className={s.deleteIcon} />
                      </button>

                      <div className={s.indexBadge}>{index + 1}</div>
                    </div>
                  </div>
                );
              })}

              {value.length < maxFiles && (
                <div
                  className={`aspect-square border-2 border-dashed border-gray-300 ${s.addRound} flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-gray-50 transition-colors`}
                  onClick={handleClickUpload}
                >
                  <FiUpload className={s.addIcon} />
                  <span className={s.addText}>Add More</span>
                  <span className={s.addRemaining}></span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className={s.progress}>
          <div className={s.progressBar}></div>
        </div>
      )}

      {/* Modal Preview (always available) */}
      <MediaPreview
        open={previewOpen}
        file={previewFile}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}